import { useState } from "react";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import { ConfirmDialog } from "@/components/admin/Fields";
import { cn } from "@/lib/utils";

/**
 * Right-side detail panel: header (eyebrow/status/title) · scrollable body · sticky footer.
 * `dirty` protects against losing edits when closing.
 */
export const DetailSheet = ({ open, onOpenChange, title, description, eyebrow, status, children, footer, dirty = false, loading = false, size = "md", testId = "admin-detail-sheet", side = "right" }) => {
  const [confirm, setConfirm] = useState(false);
  const tryChange = (o) => {
    if (!o && dirty) { setConfirm(true); return; }
    onOpenChange(o);
  };
  const width = size === "lg" ? "sm:max-w-[760px]" : size === "sm" ? "sm:max-w-[440px]" : "sm:max-w-[600px]";
  return (
    <>
      <Sheet open={open} onOpenChange={tryChange}>
        <SheetContent side={side} className={cn("flex w-full flex-col gap-0 bg-card p-0 shadow-soft", width)} data-testid={testId}>
          <SheetHeader className="hairline shrink-0 space-y-1 border-b px-4 py-3.5 pr-14 text-left sm:px-5">
            {(eyebrow || status) && (
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
                {eyebrow && <p className="t-mono-sm text-muted-foreground">{eyebrow}</p>}
                {status && <div className="flex flex-wrap items-center gap-1.5" data-testid={`${testId}-status`}>{status}</div>}
              </div>
            )}
            <SheetTitle className="font-display text-[17px] font-semibold leading-tight tracking-[-0.015em] text-balance sm:text-[18px]">{loading ? "Hentar…" : title}</SheetTitle>
            {description && !loading && <SheetDescription className="text-[12.5px]">{description}</SheetDescription>}
          </SheetHeader>
          <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4 sm:px-5" data-testid={`${testId}-body`}>
            {loading ? (
              <div className="space-y-4" aria-busy="true"><Skeleton className="h-5 w-2/3 rounded" /><Skeleton className="h-4 w-1/2 rounded" /><Skeleton className="h-24 w-full rounded-lg" /><Skeleton className="h-4 w-3/4 rounded" /></div>
            ) : children}
          </div>
          {footer && !loading && <div className="flex shrink-0 flex-wrap items-center gap-2 border-t border-border bg-background/85 px-4 py-3 backdrop-blur safe-bottom sm:px-5" data-testid={`${testId}-footer`}>{footer}</div>}
        </SheetContent>
      </Sheet>
      <ConfirmDialog open={confirm} onOpenChange={setConfirm} title="Du har ulagra endringar" text="Lukkar du panelet no, vert endringane borte." confirmLabel="Forkast endringar" destructive onConfirm={() => { setConfirm(false); onOpenChange(false); }} testId={`${testId}-discard-dialog`} />
    </>
  );
};

/** Definition list used inside DetailSheet. */
export const DetailList = ({ children, className, cols = 2 }) => (
  <dl className={cn("grid gap-x-6 gap-y-3.5 text-sm", cols === 2 && "sm:grid-cols-2", className)}>{children}</dl>
);

export const DetailItem = ({ label, children, full = false, testId, className }) => (
  <div className={cn(full && "sm:col-span-2", className)}>
    <dt className="t-mono-sm text-muted-foreground">{label}</dt>
    <dd className="mt-1 font-medium" data-testid={testId}>{children ?? "–"}</dd>
  </div>
);
